import type {
  Column,
  RawColumn,
  RelatedColumn,
  StaticValueColumn,
  TransformedColumn,
} from "./column";
import type {
  AndCondition,
  AndOrCondition,
  OrCondition,
  RootCondition,
} from "./condition";
import type { Threshold } from "../../../idr/v2/probabilistic/thresholds";

export enum OrderByType {
  property = "property",
  random = "random",
}

// TODO[splits-v2]: Delete once we migrate all customers to the new structure
/** @deprecated */
export interface LegacySplitGroup {
  groupValue: string;
  percentage: number;
}

export interface SplitGroup {
  // We *might* want to rename this to `columnValue` to match the DB; it's
  // arguably a bit more reflective of what it is (the value for this group
  // that gets injected as a column). If so, it needs to be reflected in the
  // SQL templates in splits.ts.
  groupValue: string;
  percentage: number;
  maxSize?: number | null;
  /**
   * Syncs that the split should be synced to. If this is omitted or null,
   * e.g. in previews or possibly legacy structures (TBD), this means
   * syncing to all syncs with a matching segment_id.
   */
  destinationInstanceIds?: number[] | null;
  id?: string;
  /**
   * When the split was last updated. Used to filter audience membership
   * for stable stratified splits to only include membership after split changes.
   */
  updatedAt?: Date;
}

export interface AudienceSplit {
  id?: string | undefined;
  friendly_name: string;
  column_value?: string;
  percentage: number;
  destination_instance_ids: string[];
  is_holdout_group: boolean;
}

export enum SplitSamplingType {
  NonStratified = "non-stratified",
  Stratified = "stratified",
}

export interface SplitTestDefinition {
  groupColumnName: string;
  samplingType: "non-stratified" | "stratified";
  stratificationVariables: Column[];
  splitAssignmentColumn?: RawColumn; //splitAssignmentColumn specified to split on non-primary key column
  exactCount?: boolean;
}

export interface NewSplitTestDefinition {
  groupColumnName: string;
  samplingType: "non-stratified" | "stratified";
  stratificationVariables: Column[];
  splits: AudienceSplit[];
}

// How `SizeCap.limit` is interpreted. A missing `limitType` is equivalent to
// "absolute" — readers must only branch on `limitType === "percentage"`.
export type SizeCapLimitType = "absolute" | "percentage";

// Allows us to put a cap on the size of the audience. Requires a column to
// sort by, since unordered limits might be unstable and thus aren't useful.
export interface SizeCap {
  // An absolute member count, or a percentage of matching members in
  // (0, 100] with at most 2 decimal places when limitType is "percentage".
  limit: number;
  limitType?: SizeCapLimitType;
  orderByProperty?: string | RawColumn | RelatedColumn | TransformedColumn;
  // If true, take the bottom instead of the top (i.e. sort ASC instead of DESC).
  bottom?: boolean;
  // The type of ordering - property-based or random
  orderByType?: OrderByType;
  // If true, only sort to place NULL values last (non-null values first).
  // Useful when the orderByProperty isn't directly comparable.
  onlySortByNull?: boolean;
}

export interface VisualQueryIdr {
  idrId: string;
  threshold: Threshold;
}

export interface VisualQueryFilter {
  // All conditions and condition parents require a stable id in the frontend. If it doesn't exist, it will be injected
  id?: string;

  // All the conditions must evaluate to `true` for a row to be included.
  conditions: RootCondition[];

  limit?: number;

  // Allows us to put a cap on the size of the audience. Requires a column to
  // sort by, since unordered limits might be unstable and thus aren't useful.
  sizeCap?: SizeCap;

  additionalColumns?: {
    column: RelatedColumn | TransformedColumn | StaticValueColumn;
    alias: string;
  }[];

  splitTestDefinition?: SplitTestDefinition;

  // XXX: this probably shouldn't actually go here...
  isRealtime?: boolean;

  /**
   * Passing `{ idrId: "$uuid", threshold: "strict" }` will change an IDR-model's
   * SQL from `SELECT * FROM ${idrTable}` into `SELECT * FROM ${idrTable}_strict`.
   * This matters because each IDR-generated model creates multiple output tables,
   * one per threshold.
   */
  idr?: VisualQueryIdr;
}

export interface AudienceTemplateDefinition {
  conditions: AndOrCondition<AndCondition | OrCondition>[];
}
